// Copyright ©️ 2026 eVolve MEP, LLC

import './ag-nested-grid-theme.css';

import type { StrongOmit } from '@evolvemep/api-ts';
import { Loader } from '@mantine/core';
import {
  type ColDef,
  type DefaultMenuItem,
  type GetMainMenuItemsParams,
  type GridApi,
  type MenuItemDef,
  type RowSelectionOptions,
  type SelectionColumnDef,
  themeQuartz,
} from 'ag-grid-enterprise';
import type { AgGridReactProps } from 'ag-grid-react';
import { v4 as uuidv4 } from 'uuid';

import TableViewsIcon from 'assets/icons/icon-src/table-views.svg?raw';
import ExportIcon from 'assets/icons/icon-src/user-actions/export.svg?raw';
import { HeaderWithTooltip } from 'helpers/ag-grid/HeaderWithTooltip';
import { isKeyOf } from 'helpers/isKeyOf';
import { isNil } from 'helpers/isNotNil';
import { strongIsEqual } from 'helpers/strongEntries';

import { CustomDateComponent } from './CustomDateComponent';
import { DeselectAllCheckbox } from './DeselectAllCheckbox';
import { LoadingRow } from './LoadingRow';
import { NoRowsOverlay } from './NoRowsOverlay';

export const compactRowHeight = 32 as const;

export const menuOverrides = {
  autoSizeAll: {
    name: 'Autosize All Columns',
    action: ({ api }) => {
      const colIds = api
        .getColumns()
        ?.filter((col) => !col.getColDef().pinned)
        .map((col) => col.getColId());
      api.autoSizeColumns(colIds ?? []);
    },
  },
} as const satisfies Partial<
  Record<
    DefaultMenuItem,
    {
      name: string;
      action: (params: { api: GridApi }) => void;
    }
  >
>;

const isEmptyCell = (v: unknown) => v === '' || isNil(v) || (Array.isArray(v) && v.length === 0);

/** Use as `defaultColDef` (included in `baseGridDef`) */
export const baseColumnDef: Omit<ColDef, 'field' | 'tooltipField'> = {
  suppressHeaderMenuButton: true,
  enableCellChangeFlash: true,
  minWidth: 64,
  // Default width; can be overridden (or changed on resizable columns)
  width: 200,
  valueFormatter: ({ value }) => value || '--',
  headerComponentParams: {
    innerHeaderComponent: HeaderWithTooltip,
  },
  equals: (a, b) => (isEmptyCell(a) && isEmptyCell(b)) || strongIsEqual(a, b),
} as const;

export const doNotSaveColumnIdentifier = 'doNotSave' as const;

/**
 * `ColDef` for any column which is locked to the left or right side of the table.
 * ie. buttons, dropdown, menu icons, etc
 */
export const lockedColDef = <TData,>(
  lockPosition: 'left' | 'right' = 'left',
  pinned = lockPosition === 'right',
): ColDef<TData> =>
  ({
    /** Defined to prevent the order from being randomly moved as we reload pages */
    colId: `${doNotSaveColumnIdentifier}-${uuidv4()}`,
    minWidth: 40,
    resizable: false,
    lockPosition,
    pinned: pinned ? lockPosition : undefined,
    lockPinned: pinned,
    suppressAutoSize: true,
    lockVisible: true,
    sortable: false,
    enableCellChangeFlash: false,
    suppressColumnsToolPanel: true,
    // Always re-render locked columns if any data on the row changed
    equals: () => false,
  }) as const;

export const multiRowSelection: RowSelectionOptions = {
  mode: 'multiRow',
  enableClickSelection: false,
  headerCheckbox: false,
  checkboxes: true,
};

export const getMultiRowSelection = <T,>(opts: StrongOmit<RowSelectionOptions<T>, 'mode'>): RowSelectionOptions<T> => ({
  ...multiRowSelection,
  ...opts,
});

const selectionColumnDef: SelectionColumnDef = {
  ...lockedColDef('left', true),
  headerComponent: DeselectAllCheckbox,
  width: 48,
};

// Provide getMainMenuItems or getContextMenuItems:
const getMainMenuItems = ({ column, defaultItems }: GetMainMenuItemsParams) => {
  const isLockedColumn = column?.getColId().startsWith(doNotSaveColumnIdentifier);
  const updatedMenuItems: (DefaultMenuItem | MenuItemDef)[] = isLockedColumn
    ? [menuOverrides.autoSizeAll, 'resetColumns']
    : defaultItems.map((item) => (isKeyOf(item, menuOverrides) ? menuOverrides[item] : item));
  return updatedMenuItems;
};

/** The base grid definition to use for **all** grids, including detail grids. */
export const baseGridDef: AgGridReactProps = {
  theme: themeQuartz.withParams({
    accentColor: 'var(--mantine-primary-color)',
    rowHoverColor: 'var(--mantine-color-gray-2)',
    headerBackgroundColor: 'var(--mantine-color-gray-0)',
    selectedRowBackgroundColor: 'var(--mantine-color-gray-1)',
    fontFamily: 'IBM Plex Sans',
    borderRadius: 'var(--mantine-radius-default)',
    wrapperBorderRadius: 'var(--mantine-radius-default)',
  }),
  icons: {
    export: `<img alt="export-data-icon" src="data:image/svg+xml;utf8,${encodeURIComponent(ExportIcon)}" />`,
    tableViews: `<img alt="table-views-icon" src="data:image/svg+xml;utf8,${encodeURIComponent(TableViewsIcon)}" />`,
  },
  tooltipShowMode: 'whenTruncated',
  noRowsOverlayComponent: () => <NoRowsOverlay label="No results found." />,
  loadingOverlayComponent: () => <Loader type="logo" size="xl" />,
  loadingCellRenderer: LoadingRow,
  alwaysMultiSort: true,
  keepDetailRows: true,
  detailRowAutoHeight: true,
  suppressCellFocus: true,
  suppressDragLeaveHidesColumns: true,
  tooltipShowDelay: 1000,
  popupParent: document.querySelector('body'),
  defaultColDef: baseColumnDef,
  selectionColumnDef,
  getMainMenuItems,
  enableFilterHandlers: true, // Required to be able to use AG Grid's filter sidebar panel `agNewFiltersToolPanel`.
  components: {
    // Override the default date input with our custom picker using DateOnly
    agDateInput: CustomDateComponent,
  },
  defaultExcelExportParams: {
    processCellCallback: ({ value, formatValue }) => formatValue(value),
  },
} as const;
